import type { MonospaceProject } from '#layers/data-model';
import type { CreateProjectFormOutput } from '~/pages/organization/project-create.vue';
import { createEventHook } from '@vueuse/core';
import { computed } from 'vue';
import { createError } from '#app/composables/error';
import { useRouter } from '#app/composables/router';
import { useAppConfig } from '#app/config';
import { useNuxtApp } from '#app/nuxt';
import { defineRstoreModule } from '#imports';
import { getErrorStatusCode } from '#layers/base';
import { ErrorCodes } from '#layers/base/utils/error';
import { useProjectSpaceRoutes } from '#layers/spaces';
import { SINGLETON_KEY } from '~/rstore/utils/singleton';

interface ProjectChangedHookPayload {
	oldProjectId: string | null;
	newProjectId: string | null;
}

function translate(key: string) {
	return useNuxtApp().$i18n.t(key);
}

export const useProjectStore = defineRstoreModule('monospace-studio-project', ({ store, defineState, defineMutation }) => {
	const appConfig = useAppConfig();
	const router = useRouter();
	const { loadProjectSpaceRoutes, removeProjectSpaceRoutes, updateProjectSpaceRoutes } = useProjectSpaceRoutes();

	const beforeProjectChangedHook = createEventHook<ProjectChangedHookPayload>();
	const projectChangedHook = createEventHook<ProjectChangedHookPayload>();

	let _hydrationVersion = 0;

	const state = defineState({
		currentProjectSlug: null as string | null,
	});

	const currentProjectQuery = store.MonospaceProject.query((q) => q.first(state.currentProjectSlug
		? ({
				key: state.currentProjectSlug,
			})
		: ({
				enabled: false,
			})));

	const { data: currentProject } = currentProjectQuery;

	const createProject = defineMutation((form: CreateProjectFormOutput) => store.MonospaceProject.create(form));

	return {
		currentProject,
		currentProjectSlug: computed(() => state.currentProjectSlug),
		createProject,
		hydrateProject,
		scheduleDeferredUnload,
		onBeforeProjectChanged: beforeProjectChangedHook.on,
		onProjectChanged: projectChangedHook.on,
	};

	async function hydrateProject(projectSlug: string, opts: { fromProjectRoute: boolean }) {
		// Phase 1: Invalidate any scheduled deferred unload by bumping the version.
		// This must happen before the early return, so same-project calls still cancel pending teardowns.
		++_hydrationVersion;

		if (state.currentProjectSlug === projectSlug) return;

		// Phase 2: Bump again and capture for staleness detection - if another hydrateProject()
		// call arrives while we're awaiting, the captured version goes stale, and we bail out and only go through with
		// the new project from the second, competing call.
		const version = ++_hydrationVersion;

		if (state.currentProjectSlug && appConfig.monospace.projectSlug) {
			await removeCurrentProjectRoutes();
		}

		try {
			const project = await store.MonospaceProject.findFirst({
				filter: { apiName: { _eq: projectSlug } },
			});

			if (!project) {
				await setCurrentProject(null);

				throw createError({
					statusCode: 404,
					statusMessage: translate('project.not_found'),
				});
			}

			if (version !== _hydrationVersion) return;

			await setCurrentProject(project);

			await store.MonospaceSettings.findFirst({
				key: SINGLETON_KEY,
				fetchPolicy: 'fetch-only',
			});

			if (version !== _hydrationVersion) return;

			const spaces = await loadProjectSpaceRoutes();

			if (version !== _hydrationVersion) return;

			updateProjectSpaceRoutes({
				spaces,
				skipCurrentRouteUpdate: !opts.fromProjectRoute,
			});

			// TODO filter on current user client-side or server-side
			await store.MonospaceKeyStorage.query((q) => q.many());
		}
		catch (error) {
			if (getErrorStatusCode(error) === 403) {
				throw createError({
					statusCode: 403,
					statusMessage: translate('project.no_access'),
					data: { code: ErrorCodes.PROJECT_ACCESS_DENIED },
				});
			}

			throw error;
		}
	}

	async function removeCurrentProjectRoutes() {
		await removeProjectSpaceRoutes();
	}

	function scheduleDeferredUnload() {
		const versionAtSchedule = _hydrationVersion;

		const unregister = router.afterEach(() => {
			unregister();

			if (versionAtSchedule !== _hydrationVersion) return;

			setCurrentProject(null);
		});
	}

	async function setCurrentProject(project: MonospaceProject | null) {
		const newId = project?.id ?? null;
		const oldId = currentProject.value?.id ?? null;

		if (newId === oldId) return;

		const versionAtCall = _hydrationVersion;

		await beforeProjectChangedHook.trigger({ oldProjectId: oldId, newProjectId: newId });

		// Abort stale unload — a new hydration started while hooks were running
		if (project === null && versionAtCall !== _hydrationVersion) return;

		state.currentProjectSlug = project?.apiName ?? null;
		appConfig.monospace.projectSlug = project?.apiName;

		await projectChangedHook.trigger({ oldProjectId: oldId, newProjectId: newId });
	}
});
